import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { strategiesApi, deploymentsApi, candlesApi, systemApi, adminApi, identityApi, adminIdentityApi, brokerApi, billingApi, adminBillingApi, adminProxyApi, mappedStrategiesApi, masterStrategyApi, manualTradingApi, adminWebsiteApi, adminCustomDevelopmentApi, adminPaymentsApi, adminWebsiteArticlesApi, adminGoogleReviewsApi } from './client';

import { APP_CONFIG } from '../app/config';
import { useSessionStore } from '../session/store';

const P = APP_CONFIG.poll;
const DEFAULT_SIGNAL_QUERY_PARAMS = { limit: 500, offset: 0 };
const DEFAULT_DISPATCH_QUERY_PARAMS = { limit: 500, offset: 0 };
const DEFAULT_WEBHOOK_QUERY_PARAMS = { limit: 500, offset: 0 };
const DEFAULT_RUNTIME_QUERY_PARAMS = { limit: 1000, offset: 0 };

// ─── Query Keys ────────────────────────────────────────────────────────────────
export const QK = {
  strategies:   ()       => ['strategies'],
  strategy:     (id)     => ['strategy', id],
  mappedStrategies: ()   => ['mappedStrategies'],
  mappedStrategy:   (id) => ['mappedStrategy', id],
  masterMappings:   (id) => ['masterMappings', id],
  deployment:   (id)     => ['deployment', id],
  manualTradingDeployments: () => ['manualTradingDeployments'],
  manualTradingWatchlist: (depId) => ['manualTradingWatchlist', depId],
  deployState:  (id)     => ['deployState', id],
  symbolStatus: (id)     => ['symbolStatus', id],
  runtimeSnapshot: (id)  => ['runtimeSnapshot', id],
  signals:      (id, p)  => ['signals', id, p],
  dispatch:     (id, p)  => ['dispatch', id, p],
  logs:         (id, p)  => ['logs', id, p],
  alertAudit:   (p)      => ['alertAudit', p],
  candles:      (s, tf)  => ['candles', s, tf],
  users:        ()       => ['users'],
  schemaV1:     ()       => ['strategySchemaV1'],
  schemaEnums:  ()       => ['strategySchemaEnums'],
  symbolSearch: (q, s, e)   => ['symbolSearch', q, s, e],
  health:       ()       => ['health'],
  subs:         ()       => ['subscriptions'],
  runtimeMonitoring: ()  => ['runtimeMonitoring'],
  expiryCache:      ()       => ['expiryCache'],
  adminStrategies:  (uid)   => ['adminStrategies', uid],
  brokersAll:       ()      => ['brokersAll'],
  brokersSupported: ()      => ['brokersSupported'],

  // Proxy IP binding
  proxyBinding:     (p)     => ['brokerProxyBinding', p],
  brokerMappingAvailability: (uid) => ['brokerMappingAvailability', uid ?? null],
  adminProxyInv:    ()      => ['admin', 'proxy', 'inventory'],
  adminProxyCapacity: (p)   => ['admin', 'proxy', 'capacity', p],
  adminProxyBind:   (p)     => ['admin', 'proxy', 'bindings', p],
  adminProxyReassignmentCandidates: (id) => ['admin', 'proxy', 'bindings', id, 'replacement-candidates'],
  adminProxyCooling: (p)    => ['admin', 'proxy', 'cooling', p],
  adminProxyPend:   (p)     => ['admin', 'proxy', 'pending', p],
  adminProxyIpv6Servers: () => ['admin', 'proxy', 'ipv6-servers'],
  adminProxyIpv6Run: (runId) => ['admin', 'proxy', 'ipv6-run', runId],
  // Phase E — a Proxy Server's nonterminal end-to-end IPv6 workflows.
  adminProxyIpv6ActiveRuns: (serverId) => ['admin', 'proxy', 'ipv6-active-runs', serverId ?? null],

  // Billing
  subscription:     ()      => ['billing', 'subscription'],
  subHistory:       ()      => ['billing', 'subHistory'],
  billingPlans:     ()      => ['billing', 'plans'],
  myLicensePacks:   ()      => ['billing', 'license-packs'],
  // Owner-scoped: the authenticated user id is appended by the hooks below so a
  // previous account's pending payments or Operator gateway state can never be
  // read from cache after an account switch.
  pendingPayments:  (p)     => ['billing', 'pendingPayments', p],
  operatorGateway:  ()      => ['billing', 'operatorGateway'],
  operatorBranding: ()      => ['identity', 'operatorBranding'],
  operatorLogoPreview: ()   => ['identity', 'operatorLogoPreview'],
  operatorSmtp:     ()      => ['identity', 'operatorSmtp'],
  supportContact:   ()      => ['identity', 'supportContact'],
  // Super Admin view of one Operator's branding/domain record; the viewer id is
  // appended by the hook.
  adminOperatorBranding: (opId) => ['admin', 'operatorBranding', opId ?? null],
  operatorWallet:   (opId)  => ['admin', 'billing', 'wallet', opId],
  licenseLedger:    (opId)  => ['admin', 'billing', 'ledger', opId],
  adminPlans:       (p)     => ['admin', 'billing', 'plans', p],
  adminUserSub:     (uid)   => ['admin', 'billing', 'subscription', uid],
  adminUserSubHistory: (uid)=> ['admin', 'billing', 'subHistory', uid],
  adminSubscriptions: (p)   => ['admin', 'billing', 'subscriptions', p],
  licensePacks:     (p)     => ['admin', 'billing', 'license-packs', p],
  strategyPositions: ()     => ['strategyPositions'],
  paperPositions:   (date)  => ['paperPositions', date ?? 'today'],
  // Deployment-scoped Manual Trading PAPER views (distinct from the global keys above)
  paperOrderbookByDeployment:  (deploymentId) => ['paperOrderbook', { deploymentId }],
  paperPositionsByDeployment:  (deploymentId) => ['paperPositions', { deploymentId }],
  manualRiskTriggers:          (deploymentId) => ['manualRiskTriggers', deploymentId],
  manualTradeBook:             (deploymentId, params) => ['manualTradeBook', deploymentId, params],
  manualFanoutEvents:          (params) => ['manualFanoutEvents', params],
  manualFanoutEvent:           (eventId) => ['manualFanoutEvent', eventId],

  // Payments
  adminPayments: (params)            => ['adminPayments', params],
  adminPayment:  (source, sourceId)  => ['adminPayment', source, sourceId],

  // Website Leads
  websiteLeads: (params) => ['websiteLeads', params],
  websiteLead:  (id)     => ['websiteLead', id],

  // Custom Development Strategy Requests (super-admin)
  customDevRequests: (params) => ['customDevRequests', params],
  customDevRequest:  (ref)    => ['customDevRequest', ref],
  customDevMessages: (ref, params) => ['customDevMessages', ref, params],

  // Website Articles
  websiteArticles: (params) => ['websiteArticles', params],
  websiteArticle:  (id)     => ['websiteArticle', id],

  // Google Reviews (super-admin, Stage 1B/2B)
  googleReviewsStatus:  ()     => ['admin', 'googleReviews', 'status'],
  googleReviewSettings: ()     => ['admin', 'googleReviews', 'settings'],
  googleReviews:        (p)    => ['admin', 'googleReviews', 'reviews', p],
  googleReview:         (id)   => ['admin', 'googleReviews', 'review', id],
  googleReviewLocations: ()    => ['admin', 'googleReviews', 'locations'],
};

// ─── Strategies ────────────────────────────────────────────────────────────────
export function useStrategies() {
  return useQuery({
    queryKey: QK.strategies(),
    queryFn:  strategiesApi.list,
    staleTime: P.strategies,
    retry: (failCount, error) => {
      // Don't retry 422 — it's a schema/param problem, not a transient failure
      if (error?.status === 422) return false;
      return failCount < 2;
    },
  });
}
// Manual Trading — deployed Manual Strategies (active deployments for the user)
export function useManualTradingDeployments() {
  return useQuery({
    queryKey: QK.manualTradingDeployments(),
    queryFn:  manualTradingApi.listDeployments,
    staleTime: P.deployment,
    retry: (failCount, error) => (error?.status === 422 ? false : failCount < 2),
  });
}

// Manual Trading — per-deployment watchlist rows (enabled only for active deployments).
export function useManualTradingWatchlist(deploymentId, options = {}) {
  const { enabled = true } = options;
  return useQuery({
    queryKey: QK.manualTradingWatchlist(deploymentId),
    queryFn:  () => manualTradingApi.listWatchlist(deploymentId),
    enabled:  !!deploymentId && enabled,
    staleTime: P.deployment,
    // 400 = inactive deployment, 404/422 = bad request — don't hammer these
    retry: (failCount, error) => ([400, 404, 422].includes(error?.status) ? false : failCount < 2),
  });
}
export function useCreateManualTradingWatchlistRow() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ deploymentId, payload }) => manualTradingApi.createWatchlistRow(deploymentId, payload),
    onSuccess: (_data, { deploymentId }) =>
      qc.invalidateQueries({ queryKey: QK.manualTradingWatchlist(deploymentId) }),
  });
}
export function useUpdateManualTradingWatchlistRow() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ rowId, payload }) => manualTradingApi.updateWatchlistRow(rowId, payload),
    onSuccess: (_data, { deploymentId }) =>
      qc.invalidateQueries({ queryKey: QK.manualTradingWatchlist(deploymentId) }),
  });
}
export function useDeleteManualTradingWatchlistRow() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ rowId }) => manualTradingApi.deleteWatchlistRow(rowId),
    onSuccess: (_data, { deploymentId }) =>
      qc.invalidateQueries({ queryKey: QK.manualTradingWatchlist(deploymentId) }),
  });
}

// Manual Trading — deployment-scoped PAPER orderbook (today's orders for one deployment).
export function useManualPaperOrderbook(deploymentId, { enabled = true } = {}) {
  return useQuery({
    queryKey: QK.paperOrderbookByDeployment(deploymentId),
    queryFn:  () => brokerApi.getPaperOrderbook({ deployment_id: deploymentId }),
    select:   (r) => r?.orders ?? [],
    enabled:  !!deploymentId && enabled,
    staleTime: 10_000,
    refetchInterval: 30_000,
    retry: (failCount, error) => ([400, 403, 404, 422].includes(error?.status) ? false : failCount < 1),
  });
}

// Manual Trading — deployment-scoped PAPER open positions (active view, today).
export function useManualPaperPositions(deploymentId, { enabled = true } = {}) {
  return useQuery({
    queryKey: QK.paperPositionsByDeployment(deploymentId),
    queryFn:  () => brokerApi.getPaperPositions({ deployment_id: deploymentId }),
    select:   (r) => r?.positions ?? [],
    enabled:  !!deploymentId && enabled,
    staleTime: 10_000,
    refetchInterval: 30_000,
    retry: (failCount, error) => ([400, 403, 404, 422].includes(error?.status) ? false : failCount < 1),
  });
}

// Manual Trading — submit a PAPER BUY/SELL signal. Body MUST be only { action, client_request_id }.
// On success, refresh that deployment's orderbook + positions (and the global Broker pages if mounted).
export function useSubmitManualTradingSignal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ rowId, payload }) => manualTradingApi.submitWatchlistSignal(rowId, payload),
    onSuccess: (_data, { deploymentId }) => {
      qc.invalidateQueries({ queryKey: QK.paperOrderbookByDeployment(deploymentId) });
      qc.invalidateQueries({ queryKey: QK.paperPositionsByDeployment(deploymentId) });
      // Refetch the watchlist so the newly persisted last_signal_action / last_trigger_price
      // (backend F1) arrive and the immediate local overlay can be reconciled/cleared.
      qc.invalidateQueries({ queryKey: QK.manualTradingWatchlist(deploymentId) });
      // Prefix-invalidate the global Broker orderbook/positions views too (no-op if not mounted).
      qc.invalidateQueries({ queryKey: ['paperOrderbook'] });
      qc.invalidateQueries({ queryKey: ['paperPositions'] });
    },
  });
}

// Manual Trading LIVE — step 1: prepare a protected LIMIT order. Body { action }. No invalidation (prepare
// writes nothing); no auto-retry (preserve backend detail). The caller holds the prepared_token in memory.
export function usePrepareManualTradingOrder() {
  return useMutation({
    mutationFn: ({ rowId, payload }) => manualTradingApi.prepareWatchlistOrder(rowId, payload),
    retry: false,
  });
}

// Manual Trading PAPER/LIVE — step 2: confirm the prepared order. Privileged MASTER_MANUAL flows may
// add fanout_scope. No auto-retry. On success, refresh orderbook/positions/risk/watchlist/fan-out events
// exactly like the PAPER signal (truth refresh; no fake fill).
export function useConfirmManualTradingOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ rowId, payload }) => manualTradingApi.confirmWatchlistOrder(rowId, payload),
    retry: false,
    onSuccess: (_data, { deploymentId }) => {
      qc.invalidateQueries({ queryKey: QK.paperOrderbookByDeployment(deploymentId) });
      qc.invalidateQueries({ queryKey: QK.paperPositionsByDeployment(deploymentId) });
      qc.invalidateQueries({ queryKey: QK.manualRiskTriggers(deploymentId) });
      qc.invalidateQueries({ queryKey: QK.manualTradingWatchlist(deploymentId) });
      qc.invalidateQueries({ queryKey: ['paperOrderbook'] });
      qc.invalidateQueries({ queryKey: ['paperPositions'] });
      qc.invalidateQueries({ queryKey: ['manualFanoutEvents'] });
    },
  });
}

// Manual Trading — full PAPER square-off of one position (row-based). Body MUST be only
// { client_request_id }. Refreshes the same deployment's orderbook + positions on success.
export function useExitManualTradingPosition() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ rowId, payload }) => manualTradingApi.exitWatchlistPosition(rowId, payload),
    onSuccess: (_data, { deploymentId }) => {
      qc.invalidateQueries({ queryKey: QK.paperOrderbookByDeployment(deploymentId) });
      qc.invalidateQueries({ queryKey: QK.paperPositionsByDeployment(deploymentId) });
      qc.invalidateQueries({ queryKey: ['paperOrderbook'] });
      qc.invalidateQueries({ queryKey: ['paperPositions'] });
    },
  });
}

// Manual Trading LIVE exit — step 1: preview the protected LIMIT exit. No invalidation (prepare writes
// nothing); no auto-retry (preserve backend detail). The caller holds the prepared_token in memory.
export function usePrepareManualTradingExit() {
  return useMutation({
    mutationFn: ({ rowId }) => manualTradingApi.prepareWatchlistExit(rowId),
    retry: false,
  });
}

// Manual Trading LIVE exit — step 2: confirm. Body { client_request_id, prepared_token, limit_price,
// price_mode }. No auto-retry. On success refresh orderbook/positions/risk/watchlist like the entry confirm.
export function useConfirmManualTradingExit() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ rowId, payload }) => manualTradingApi.confirmWatchlistExit(rowId, payload),
    retry: false,
    onSuccess: (_data, { deploymentId }) => {
      qc.invalidateQueries({ queryKey: QK.paperOrderbookByDeployment(deploymentId) });
      qc.invalidateQueries({ queryKey: QK.paperPositionsByDeployment(deploymentId) });
      qc.invalidateQueries({ queryKey: QK.manualRiskTriggers(deploymentId) });
      qc.invalidateQueries({ queryKey: QK.manualTradingWatchlist(deploymentId) });
      qc.invalidateQueries({ queryKey: ['paperOrderbook'] });
      qc.invalidateQueries({ queryKey: ['paperPositions'] });
    },
  });
}

// Manual Trading LIVE order cancel — step 1: preview the cancelable pending qty. No invalidation
// (prepare writes nothing); no auto-retry. Caller holds the prepared_token in memory.
export function usePrepareManualTradingCancel() {
  return useMutation({
    mutationFn: ({ orderLogId }) => manualTradingApi.prepareOrderCancel(orderLogId),
    retry: false,
  });
}

// Manual Trading LIVE order cancel — step 2: confirm. Body { client_request_id, prepared_token }.
// No auto-retry. On success refresh orderbook/positions/risk/watchlist (same set as exit confirm).
export function useConfirmManualTradingCancel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ orderLogId, payload }) => manualTradingApi.confirmOrderCancel(orderLogId, payload),
    retry: false,
    onSuccess: (_data, { deploymentId }) => {
      qc.invalidateQueries({ queryKey: QK.paperOrderbookByDeployment(deploymentId) });
      qc.invalidateQueries({ queryKey: QK.paperPositionsByDeployment(deploymentId) });
      qc.invalidateQueries({ queryKey: QK.manualRiskTriggers(deploymentId) });
      qc.invalidateQueries({ queryKey: QK.manualTradingWatchlist(deploymentId) });
      qc.invalidateQueries({ queryKey: ['paperOrderbook'] });
      qc.invalidateQueries({ queryKey: ['paperPositions'] });
    },
  });
}

// Manual Trading — active risk triggers for a deployment (bare array, newest first).
export function useManualRiskTriggers(deploymentId, { enabled = true } = {}) {
  return useQuery({
    queryKey: QK.manualRiskTriggers(deploymentId),
    queryFn:  () => manualTradingApi.getRiskTriggers(deploymentId),
    select:   (r) => (Array.isArray(r) ? r : (r?.triggers ?? [])),
    enabled:  !!deploymentId && enabled,
    staleTime: 10_000,
    refetchInterval: 30_000,
    retry: (failCount, error) => ([400, 403, 404, 422].includes(error?.status) ? false : failCount < 1),
  });
}

// Manual Trading — update the OPEN position's active risk trigger. Body MUST be only
// { target_trigger_price } OR { stop_loss_trigger_price }. Not a watchlist edit. Refreshes the
// deployment's risk triggers + paper positions on success.
export function useUpdateManualActiveRisk() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ rowId, payload }) => manualTradingApi.updateActiveRisk(rowId, payload),
    onSuccess: (_data, { deploymentId }) => {
      qc.invalidateQueries({ queryKey: QK.manualRiskTriggers(deploymentId) });
      qc.invalidateQueries({ queryKey: QK.paperPositionsByDeployment(deploymentId) });
    },
  });
}

// Manual Trading — Exit All open PAPER positions for a deployment. Body is only { client_request_id }
// (the caller supplies a stable id per confirmed attempt). No auto-retry (preserve backend detail). On
// success, conservatively mirror the app-event invalidation for THIS deployment (fallback if the WS event
// is delayed/unavailable): Orders, Positions, Risk Triggers, and every Trade Book variant.
export function useManualExitAll() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ deploymentId, clientRequestId }) =>
      manualTradingApi.exitAllManualPositions(deploymentId, { client_request_id: clientRequestId }),
    retry: false,
    onSuccess: (_data, { deploymentId }) => {
      qc.invalidateQueries({ queryKey: QK.paperOrderbookByDeployment(deploymentId) });
      qc.invalidateQueries({ queryKey: ['paperOrderbook'] });
      qc.invalidateQueries({ queryKey: QK.paperPositionsByDeployment(deploymentId) });
      qc.invalidateQueries({ queryKey: ['paperPositions'] });
      qc.invalidateQueries({ queryKey: QK.manualRiskTriggers(deploymentId) });
      qc.invalidateQueries({ queryKey: ['manualTradeBook', deploymentId], exact: false });
    },
  });
}

// Manual Trading — backend-reconstructed Trade Book (read-only). `params` MUST be a memoized object
// (date/status/page/page_size, + symbol only when filtering) so the query key stays stable. The caller
// supplies refetchInterval (poll only for the current date while the tab is active; false otherwise).
export function useManualTradeBook(deploymentId, params, { enabled = true, refetchInterval = false } = {}) {
  return useQuery({
    queryKey: QK.manualTradeBook(deploymentId, params),
    queryFn:  () => manualTradingApi.getTradeBook(deploymentId, params),
    enabled:  !!deploymentId && enabled,
    placeholderData: (previous) => previous, // keep prior page/filter data during pagination/filter changes
    staleTime: 10_000,
    refetchInterval,
    refetchOnWindowFocus: true,
    retry: (failCount, error) => ([400, 403, 404, 409, 422].includes(error?.status) ? false : failCount < 1),
  });
}

// Operator/admin Manual fan-out observability. Queries remain disabled until the owning UI is opened.
export function useManualFanoutEvents(params, { enabled = true } = {}) {
  return useQuery({
    queryKey: QK.manualFanoutEvents(params),
    queryFn: () => manualTradingApi.listFanoutEvents(params),
    enabled,
    placeholderData: (previous) => previous,
    staleTime: 10_000,
    refetchOnWindowFocus: true,
    retry: (failCount, error) => ([400, 403, 404, 422].includes(error?.status) ? false : failCount < 1),
  });
}

export function useManualFanoutEventDetail(eventId, { enabled = true } = {}) {
  return useQuery({
    queryKey: QK.manualFanoutEvent(eventId),
    queryFn: () => manualTradingApi.getFanoutEvent(eventId),
    enabled: !!eventId && enabled,
    staleTime: 10_000,
    retry: (failCount, error) => ([400, 403, 404, 422].includes(error?.status) ? false : failCount < 1),
  });
}
export function useStrategy(id) {
  return useQuery({ queryKey: QK.strategy(id), queryFn: () => strategiesApi.get(id), enabled: !!id });
}
export function useStrategyPositions() {
  return useQuery({
    queryKey: QK.strategyPositions(),
    queryFn:  strategiesApi.getPositions,
    staleTime: 10_000,
    refetchInterval: 15_000,
    retry: 1,
  });
}
export function usePaperPositions(date /* 'YYYY-MM-DD' | null | undefined */, { enabled = true } = {}) {
  const isHistorical = !!date;
  return useQuery({
    queryKey: QK.paperPositions(date),
    queryFn: () => brokerApi.getPaperPositions(date ? { date, include_closed: true } : undefined),
    enabled,
    staleTime: isHistorical ? Infinity : 30_000,
    refetchInterval: isHistorical ? false : 30_000,
    retry: 1,
  });
}
export function useBrokerAll(enabled = true) {
  return useQuery({
    queryKey: QK.brokersAll(),
    queryFn: brokerApi.getAll,
    enabled,
    staleTime: 60_000,
  });
}
export function useBrokerSupported(enabled = true) {
  return useQuery({
    queryKey: QK.brokersSupported(),
    queryFn: brokerApi.getSupported,
    enabled,
    staleTime: 60_000,
  });
}
export function useCreateStrategy() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: strategiesApi.create, onSuccess: () => qc.invalidateQueries({ queryKey: QK.strategies() }) });
}
export function useCreateStrategyV2() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: strategiesApi.createV2, onSuccess: () => qc.invalidateQueries({ queryKey: QK.strategies() }) });
}
export function useReserveStrategyToken() {
  return useMutation({ mutationFn: strategiesApi.reserveToken });
}
export function useUpdateStrategyV2(id) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload) => strategiesApi.updateV2(id, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QK.strategies() });
      if (id) qc.invalidateQueries({ queryKey: QK.strategy(id) });
    },
  });
}
export function useUpdateStrategy() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }) => strategiesApi.updateV2(id, payload),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: QK.strategies() });
      if (vars?.id) qc.invalidateQueries({ queryKey: QK.strategy(vars.id) });
    },
  });
}
export function useDeployStrategy() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: ({ id, payload }) => strategiesApi.deploy(id, payload), onSuccess: () => qc.invalidateQueries({ queryKey: QK.strategies() }) });
}

// ─── Mapped Master Strategies ─────────────────────────────────────────────────
// Swallow 404/405/501 to an empty list so missing backend endpoints stay invisible
// in the UI (mapped section simply empty; STANDARD flow unaffected).
export function useMappedStrategies() {
  return useQuery({
    queryKey: QK.mappedStrategies(),
    queryFn: async () => {
      try {
        const res = await mappedStrategiesApi.list();
        const list = Array.isArray(res)
          ? res
          : (res?.mappings ?? res?.items ?? []);
        return Array.isArray(list) ? list : [];
      } catch (err) {
        if (err?.status === 404 || err?.status === 405 || err?.status === 501) return [];
        throw err;
      }
    },
    staleTime: P.strategies,
    retry: false,
  });
}

export function useUpdateMappedStrategy(mappingId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload) => mappedStrategiesApi.update(mappingId, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QK.mappedStrategies() });
      if (mappingId) qc.invalidateQueries({ queryKey: QK.mappedStrategy(mappingId) });
    },
  });
}

// ─── Operator-side master-strategy management ────────────────────────────────
export function useMasterStrategyMappings(strategyId, enabled = true) {
  return useQuery({
    queryKey: QK.masterMappings(strategyId),
    queryFn: () => masterStrategyApi.listMappings(strategyId),
    enabled: !!strategyId && enabled,
    staleTime: P.strategies,
    retry: false,
  });
}

export function useEnableMasterStrategy() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (strategyId) => masterStrategyApi.enable(strategyId),
    onSuccess: async (_data, strategyId) => {
      await Promise.all([
        qc.invalidateQueries({ queryKey: QK.strategies() }),
        qc.invalidateQueries({ queryKey: QK.manualTradingDeployments() }),
        ...(strategyId ? [
          qc.invalidateQueries({ queryKey: QK.strategy(strategyId) }),
          qc.invalidateQueries({ queryKey: ['masterStatus', strategyId] }),
        ] : []),
      ]);
    },
  });
}

export function useCreateMasterMapping(strategyId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (users) => masterStrategyApi.createMappings(strategyId, users),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QK.masterMappings(strategyId) });
    },
  });
}

export function useUpdateMasterMapping(strategyId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ mapping_id, payload }) => masterStrategyApi.updateMapping(strategyId, mapping_id, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QK.masterMappings(strategyId) });
    },
  });
}

export function useMasterStrategyStatus(strategyId, enabled = true) {
  return useQuery({
    queryKey: ['masterStatus', strategyId],
    queryFn: () => masterStrategyApi.getStatus(strategyId),
    enabled: !!strategyId && enabled,
    staleTime: 10_000,
  });
}

export function useActivateMasterStrategy() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => masterStrategyApi.activate(id),
    onSuccess: async (_d, id) => {
      await Promise.all([
        qc.invalidateQueries({ queryKey: QK.strategies() }),
        qc.invalidateQueries({ queryKey: ['masterStatus', id] }),
        qc.invalidateQueries({ queryKey: QK.manualTradingDeployments() }),
      ]);
    },
  });
}

export function useDeactivateMasterStrategy() {
  const qc = useQueryClient();
  return useMutation({
    // Variants: pass plain id (no confirm), or { id, confirm_open_positions }.
    mutationFn: (variables) => {
      if (typeof variables === 'object' && variables !== null) {
        const { id, ...body } = variables;
        return masterStrategyApi.deactivate(id, body);
      }
      return masterStrategyApi.deactivate(variables);
    },
    onSuccess: async (_d, variables) => {
      const id = (typeof variables === 'object' && variables !== null) ? variables.id : variables;
      await Promise.all([
        qc.invalidateQueries({ queryKey: QK.strategies() }),
        qc.invalidateQueries({ queryKey: ['masterStatus', id] }),
        qc.invalidateQueries({ queryKey: QK.manualTradingDeployments() }),
      ]);
    },
  });
}

export function useMasterForceExitCandidates(strategyId, enabled = true) {
  return useQuery({
    queryKey: ['masterForceExitCandidates', strategyId],
    queryFn: () => masterStrategyApi.getForceExitCandidates(strategyId),
    enabled: !!strategyId && enabled,
    staleTime: 5_000,
  });
}

export function useMasterForceExitUsers(strategyId, symbol) {
  return useQuery({
    queryKey: ['masterForceExitUsers', strategyId, symbol],
    queryFn: () => masterStrategyApi.getForceExitUsers(strategyId, symbol),
    enabled: !!strategyId && !!symbol,
    staleTime: 5_000,
  });
}

export function useMasterForceExit() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ strategyId, payload }) => masterStrategyApi.forceExit(strategyId, payload),
    onSuccess: (_d, { strategyId }) => {
      qc.invalidateQueries({ queryKey: ['masterForceExitCandidates', strategyId] });
      qc.invalidateQueries({ queryKey: QK.strategies() });
    },
  });
}

// ─── Deployments ───────────────────────────────────────────────────────────────
export function useDeployment(id) {
  return useQuery({ queryKey: QK.deployment(id), queryFn: () => deploymentsApi.get(id), enabled: !!id, refetchInterval: P.deployment });
}
export function useDeploymentState(id) {
  return useQuery({ queryKey: QK.deployState(id), queryFn: () => deploymentsApi.getState(id), enabled: !!id, refetchInterval: P.deployment });
}
export function useDeploymentSymbolStatus(id) {
  return useQuery({ queryKey: QK.symbolStatus(id), queryFn: () => deploymentsApi.getSymbolStatus(id), enabled: !!id, refetchInterval: P.deployment });
}
export function useRuntimeSnapshot(id, options = {}) {
  const enabled = options.enabled !== false;
  return useQuery({
    queryKey: QK.runtimeSnapshot(id),
    queryFn: () => deploymentsApi.getRuntimeSnapshot(id),
    enabled: !!id && enabled,
    refetchInterval: P.deployment,
  });
}
export function useSignals(id, params) {
  const effectiveParams = { ...DEFAULT_SIGNAL_QUERY_PARAMS, ...(params || {}) };
  return useQuery({
    queryKey: QK.signals(id, effectiveParams),
    queryFn: () => deploymentsApi.getSignals(id, effectiveParams),
    enabled: !!id,
    refetchInterval: P.signals,
  });
}
export function useDispatchLogs(id, params) {
  const effectiveParams = { ...DEFAULT_DISPATCH_QUERY_PARAMS, ...(params || {}) };
  return useQuery({
    queryKey: QK.dispatch(id, effectiveParams),
    queryFn: () => deploymentsApi.getDispatch(id, effectiveParams),
    enabled: !!id,
    refetchInterval: P.dispatch,
  });
}
export function useWebhookEvents(id, params) {
  const effectiveParams = { ...DEFAULT_WEBHOOK_QUERY_PARAMS, ...(params || {}) };
  return useQuery({
    queryKey: ['webhookEvents', id, effectiveParams],
    queryFn: () => deploymentsApi.getWebhookEvents(id, effectiveParams),
    enabled: !!id,
    refetchInterval: P.signals,
  });
}
export function useRuntimeLogs(id, params) {
  const effectiveParams = { ...DEFAULT_RUNTIME_QUERY_PARAMS, ...(params || {}) };
  return useQuery({
    queryKey: QK.logs(id, effectiveParams),
    queryFn: () => deploymentsApi.getLogs(id, effectiveParams),
    enabled: !!id,
    refetchInterval: P.logs,
  });
}
export function useAlertAudit(params) {
  return useQuery({
    queryKey: QK.alertAudit(params),
    queryFn: () => deploymentsApi.getAlertAudit(params),
    refetchInterval: P.logs,
  });
}
export function usePauseDeployment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => deploymentsApi.pause(id),
    onSuccess: (_, id) => {
      qc.invalidateQueries({ queryKey: QK.deployment(id) });
      qc.invalidateQueries({ queryKey: QK.strategies() });
    },
  });
}
export function useResumeDeployment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => deploymentsApi.resume(id),
    onSuccess: (_, id) => {
      qc.invalidateQueries({ queryKey: QK.deployment(id) });
      qc.invalidateQueries({ queryKey: QK.deployState(id) });
      qc.invalidateQueries({ queryKey: QK.symbolStatus(id) });
      qc.invalidateQueries({ queryKey: QK.strategies() });
    },
  });
}
export function useStopDeployment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => deploymentsApi.stop(id),
    onSuccess: (_, id) => {
      qc.invalidateQueries({ queryKey: QK.deployment(id) });
      qc.invalidateQueries({ queryKey: QK.deployState(id) });
      qc.invalidateQueries({ queryKey: QK.symbolStatus(id) });
      qc.invalidateQueries({ queryKey: QK.strategies() });
      qc.invalidateQueries({ queryKey: QK.mappedStrategies() });
    },
  });
}
export function useExitAndStop() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => deploymentsApi.exitAndStop(id),
    onSuccess: (_, id) => {
      qc.invalidateQueries({ queryKey: QK.deployment(id) });
      qc.invalidateQueries({ queryKey: QK.deployState(id) });
      qc.invalidateQueries({ queryKey: QK.symbolStatus(id) });
      qc.invalidateQueries({ queryKey: QK.strategies() });
      qc.invalidateQueries({ queryKey: QK.mappedStrategies() });
    },
  });
}
export function useExitAndStartDeployment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, targetMode }) => deploymentsApi.exitAndStart(id, targetMode),
    onSuccess: (_, { id }) => {
      qc.invalidateQueries({ queryKey: QK.deployment(id) });
      qc.invalidateQueries({ queryKey: QK.deployState(id) });
      qc.invalidateQueries({ queryKey: QK.symbolStatus(id) });
      qc.invalidateQueries({ queryKey: QK.strategies() });
    },
  });
}
export function useDeleteDeployment() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: (id) => deploymentsApi.delete(id), onSuccess: () => qc.invalidateQueries({ queryKey: QK.strategies() }) });
}
export function useDeleteStrategy() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: (id) => strategiesApi.delete(id), onSuccess: () => qc.invalidateQueries({ queryKey: QK.strategies() }) });
}

// ─── Candles ───────────────────────────────────────────────────────────────────
export function useCandles(symbol, timeframe, params) {
  return useQuery({ queryKey: QK.candles(symbol, timeframe), queryFn: () => candlesApi.get(symbol, timeframe, params), enabled: !!(symbol && timeframe), refetchInterval: 60_000 });
}


export function useStrategySchemaV1() {
  return useQuery({ queryKey: QK.schemaV1(), queryFn: strategiesApi.schemaV1, staleTime: 300_000 });
}
export function useStrategySchemaEnums() {
  return useQuery({ queryKey: QK.schemaEnums(), queryFn: strategiesApi.schemaEnums, staleTime: 300_000 });
}
export function useStrategyConditionCatalog() {
  return useQuery({ queryKey: ['strategy', 'schema', 'v1', 'condition-catalog'], queryFn: strategiesApi.conditionCatalog, staleTime: 300_000 });
}
export function useEvaluateConditionTree() {
  return useMutation({ mutationFn: strategiesApi.evaluateConditionTree });
}
export function useSymbolSearch(q, segment = 'all', exchange = 'all', enabled = true) {
  return useQuery({
    queryKey: QK.symbolSearch(q, segment, exchange),
    queryFn: () => systemApi.symbolSearch({ q, segment, exchange }),
    enabled,
    staleTime: 30_000,
    placeholderData: (previous) => previous,
    retry: 1,
  });
}

// ─── System ────────────────────────────────────────────────────────────────────
export function useSystemHealth() {
  return useQuery({ queryKey: QK.health(), queryFn: adminApi.systemHealth, refetchInterval: P.health, retry: false });
}
export function useSubscriptions() {
  return useQuery({ queryKey: QK.subs(), queryFn: systemApi.subscriptions, refetchInterval: 10_000 });
}
export function useRuntimeMonitoring() {
  return useQuery({ queryKey: QK.runtimeMonitoring(), queryFn: adminApi.runtimeMonitoring, refetchInterval: P.health, retry: false });
}
export function useAdminUsers() {
  return useQuery({ queryKey: ['adminUsers'], queryFn: adminApi.allUsers, staleTime: 30_000 });
}
export function useExpiryCache() {
  return useQuery({ queryKey: QK.expiryCache(), queryFn: adminApi.expiryCache, staleTime: 60_000 });
}
export function useAdminStrategiesByUser(userId) {
  return useQuery({
    queryKey: QK.adminStrategies(userId),
    queryFn:  () => adminApi.strategiesForUser(userId),
    enabled:  !!userId,
    staleTime: 30_000,
    retry: false,
  });
}

export function useAdminPositionSummary(userId) {
  return useQuery({
    queryKey: ['adminPositionSummary', userId],
    queryFn:  () => adminApi.positionSummary(userId),
    enabled:  !!userId,
    staleTime: 15_000,
    retry: false,
  });
}

export function useMarkDeploymentFlat() {
  const qc = useQueryClient();
  return useMutation({
    // Variables: { deploymentId, action?, idempotencyKey? } — an absent action
    // is the backend INSPECT default (no body, no Idempotency-Key header).
    // Explicit actions are idempotency-keyed mutating operations: never
    // auto-retry, because a retry must reuse the caller's exact same key.
    mutationFn: ({ deploymentId, action, idempotencyKey }) =>
      adminApi.markDeploymentFlat(deploymentId, { action, idempotencyKey }),
    retry: false,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['adminPositionSummary'] });
    },
  });
}

export function useDismissOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (orderId) => adminApi.dismissOrder(orderId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['adminPositionSummary'] });
    },
  });
}

// ─── Identity / Auth ───────────────────────────────────────────────────────────
export function useMe() {
  return useQuery({ queryKey: ['me'], queryFn: identityApi.me, staleTime: 60_000 });
}
export function useUpdateMe() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: identityApi.updateMe, onSuccess: () => qc.invalidateQueries({ queryKey: ['me'] }) });
}
export function useChangePassword() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: identityApi.changePassword,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['me'] }),
  });
}

// ─── Admin Identity ────────────────────────────────────────────────────────────
// The backend scopes these lists/records to the caller (an Operator sees only
// assigned customers). The viewer id is appended so a previous session's
// scoped list can never be served from cache; invalidations use the prefix.
export function useAdminIdentityUsers() {
  const viewerId = useSessionStore((s) => s.user?.id ?? null);
  return useQuery({ queryKey: ['adminIdentityUsers', viewerId], queryFn: adminIdentityApi.listUsers, staleTime: 30_000 });
}
export function useCreateAdminUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: adminIdentityApi.createUser,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['adminIdentityUsers'] });
      qc.invalidateQueries({ queryKey: ['adminUsers'] });
    },
  });
}
export function useAdminIdentityUser(id, { enabled = true } = {}) {
  const viewerId = useSessionStore((s) => s.user?.id ?? null);
  return useQuery({
    queryKey: ['adminIdentityUser', id, viewerId],
    queryFn: () => adminIdentityApi.getUser(id),
    enabled: enabled && !!id,
  });
}

// Super Admin custom-domain management for one Operator. The id must come from
// the authorized admin user record; the backend enforces Super Admin access.
export function useAdminOperatorBranding(operatorId, { enabled = true } = {}) {
  const viewerId = useSessionStore((s) => s.user?.id ?? null);
  const validId = Number.isInteger(operatorId) && operatorId > 0;
  return useQuery({
    queryKey: [...QK.adminOperatorBranding(operatorId), viewerId],
    queryFn: () => adminIdentityApi.operatorBranding(operatorId),
    enabled: enabled && validId,
    staleTime: 15_000,
    retry: false,
  });
}

export function useUpdateOperatorDomainLifecycle() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ operatorId, action }) => adminIdentityApi.updateOperatorDomainLifecycle(operatorId, action),
    onSettled: (_data, _err, { operatorId } = {}) => qc.invalidateQueries({ queryKey: QK.adminOperatorBranding(operatorId) }),
  });
}
export function useUpdateUserProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...payload }) => adminIdentityApi.updateUserProfile(id, payload),
    onSuccess: (_, { id }) => {
      qc.invalidateQueries({ queryKey: ['adminIdentityUser', id] });
      qc.invalidateQueries({ queryKey: ['adminIdentityUsers'] });
      qc.invalidateQueries({ queryKey: ['adminUsers'] });
    },
  });
}
export function useSetUserStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, status }) => adminIdentityApi.setUserStatus(id, status),
    onSuccess: (_, { id }) => {
      qc.invalidateQueries({ queryKey: ['adminIdentityUser', id] });
      qc.invalidateQueries({ queryKey: ['adminIdentityUsers'] });
      qc.invalidateQueries({ queryKey: ['adminUsers'] });
    },
  });
}
export function useAdminRoles() {
  return useQuery({ queryKey: ['adminRoles'], queryFn: adminIdentityApi.listRoles, staleTime: 300_000 });
}
export function useAssignRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, role }) => adminIdentityApi.assignRole(userId, role),
    onSuccess: (_, { userId }) => {
      qc.invalidateQueries({ queryKey: ['adminIdentityUser', userId] });
      qc.invalidateQueries({ queryKey: ['adminIdentityUsers'] });
      qc.invalidateQueries({ queryKey: ['adminUsers'] });
    },
  });
}
export function useRemoveRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, roleNameOrId }) => adminIdentityApi.removeRole(userId, roleNameOrId),
    onSuccess: (_, { userId }) => {
      qc.invalidateQueries({ queryKey: ['adminIdentityUser', userId] });
      qc.invalidateQueries({ queryKey: ['adminIdentityUsers'] });
      qc.invalidateQueries({ queryKey: ['adminUsers'] });
    },
  });
}
export function useSetOperatorLimits() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, payload }) => adminIdentityApi.setOperatorLimits(userId, payload),
    onSuccess: (_, { userId }) => {
      qc.invalidateQueries({ queryKey: ['adminIdentityUser', userId] });
      qc.invalidateQueries({ queryKey: ['adminIdentityUsers'] });
      qc.invalidateQueries({ queryKey: ['adminUsers'] });
    },
  });
}
export function useReassignOperator() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, operatorId }) => adminIdentityApi.reassignOperator(userId, operatorId),
    onSuccess: (_, { userId }) => {
      qc.invalidateQueries({ queryKey: ['adminIdentityUser', userId] });
      qc.invalidateQueries({ queryKey: ['adminIdentityUsers'] });
      qc.invalidateQueries({ queryKey: ['adminUsers'] });
    },
  });
}
export function useDeleteUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, mode = 'soft' }) => adminIdentityApi.deleteUser(id, mode),
    onSuccess: (_, { id }) => {
      qc.invalidateQueries({ queryKey: ['adminIdentityUsers'] });
      qc.invalidateQueries({ queryKey: ['adminUsers'] });
      qc.removeQueries({ queryKey: ['adminIdentityUser', id] });
    },
  });
}

// ─── Broker ────────────────────────────────────────────────────────────────────
export function useBrokerCurrent(userId) {
  const currentUserId = useSessionStore((s) => s.user?.id);
  const queryUserId = userId ?? currentUserId ?? null;
  return useQuery({
    queryKey: ['brokerCurrent', queryUserId],
    queryFn: () => brokerApi.current(userId),
    staleTime: 30_000,
    retry: (failCount, error) => {
      // Don't retry 403 (permission) or 404 (not found)
      if (error?.status === 403 || error?.status === 404) return false;
      return failCount < 2;
    },
  });
}
// Resolves the actual user a broker mutation acted on, for identity-safe
// cache invalidation. Self-service call sites never pass userId, so this
// falls back to the current session user rather than invalidating broadly.
function resolveBrokerMutationTargetUserId(variables, currentUserId) {
  const explicit = variables && typeof variables === 'object' ? variables.userId : undefined;
  return explicit ?? currentUserId ?? null;
}
export function useMapBroker() {
  const qc = useQueryClient();
  const currentUserId = useSessionStore((s) => s.user?.id);
  return useMutation({
    // userId (admin/operator target-user flows) is routed to the query param by brokerApi.map;
    // the rest of the payload is the existing map request body, unchanged for self-mapping.
    mutationFn: ({ userId, ...payload } = {}) => brokerApi.map(payload, userId),
    onSuccess: (_data, variables) => {
      const targetUserId = resolveBrokerMutationTargetUserId(variables, currentUserId);
      qc.invalidateQueries({ queryKey: ['brokerCurrent'] });
      qc.invalidateQueries({ queryKey: QK.proxyBinding() });
      qc.invalidateQueries({ queryKey: QK.brokerMappingAvailability(targetUserId) });
    },
    onError: (_error, variables) => {
      // Availability may have changed between load and map (another user took the
      // last route) — refetch only the affected target's availability, never all users'.
      const targetUserId = resolveBrokerMutationTargetUserId(variables, currentUserId);
      qc.invalidateQueries({ queryKey: QK.brokerMappingAvailability(targetUserId) });
    },
  });
}
export function useUnmapBroker() {
  const qc = useQueryClient();
  const currentUserId = useSessionStore((s) => s.user?.id);
  return useMutation({
    mutationFn: (payload) => {
      if (payload && typeof payload === 'object' && payload.brokerName) {
        return brokerApi.unmapCredential(payload);
      }
      return brokerApi.unmap(payload);
    },
    onSuccess: (_data, variables) => {
      const targetUserId = resolveBrokerMutationTargetUserId(variables, currentUserId);
      qc.invalidateQueries({ queryKey: ['brokerCurrent'] });
      qc.invalidateQueries({ queryKey: QK.proxyBinding() });
      qc.invalidateQueries({ queryKey: ['me'] });
      qc.invalidateQueries({ queryKey: QK.brokerMappingAvailability(targetUserId) });
    },
  });
}
export function useUpdateBrokerCredentials() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: brokerApi.updateCredentials,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['brokerCurrent'] });
      qc.invalidateQueries({ queryKey: QK.proxyBinding() });
    },
  });
}
export function useBrokerMappingAvailability(userId) {
  const currentUserId = useSessionStore((s) => s.user?.id);
  const queryUserId = userId ?? currentUserId ?? null;
  return useQuery({
    queryKey: QK.brokerMappingAvailability(queryUserId),
    queryFn: () => brokerApi.getMappingAvailability(userId),
    staleTime: 15_000,
    retry: 1,
  });
}

// ─── All-deployment variants (no ID required) ──────────────────────────────
export function useAllSignals(params) {
  const effectiveParams = { ...DEFAULT_SIGNAL_QUERY_PARAMS, ...(params || {}) };
  return useQuery({
    queryKey: ['allSignals', effectiveParams],
    queryFn: () => deploymentsApi.getAllSignals(effectiveParams),
    refetchInterval: P.signals,
  });
}
export function useAllDispatchLogs() {
  return useQuery({
    queryKey: ['allDispatch', DEFAULT_DISPATCH_QUERY_PARAMS],
    queryFn: () => deploymentsApi.getAllDispatch(DEFAULT_DISPATCH_QUERY_PARAMS),
    refetchInterval: P.dispatch,
  });
}
export function useAllWebhookEvents(params) {
  const effectiveParams = { ...DEFAULT_WEBHOOK_QUERY_PARAMS, ...(params || {}) };
  return useQuery({
    queryKey: ['allWebhookEvents', effectiveParams],
    queryFn: () => deploymentsApi.getAllWebhookEvents(effectiveParams),
    refetchInterval: P.signals,
  });
}
export function useAllRuntimeLogs(params) {
  const effectiveParams = { ...DEFAULT_RUNTIME_QUERY_PARAMS, ...(params || {}) };
  return useQuery({
    queryKey: ['allLogs', effectiveParams],
    queryFn: () => deploymentsApi.getAllLogs(effectiveParams),
    refetchInterval: P.logs,
  });
}

export function useTradingAudit(params) {
  return useQuery({
    queryKey: ['tradingAudit', params],
    queryFn: () => adminApi.tradingAudit(params),
    staleTime: 15_000,
    refetchInterval: 15_000,
  });
}

// ─── Billing — User ────────────────────────────────────────────────────────────
export function useMySubscription() {
  const currentUserId = useSessionStore((s) => s.user?.id);
  return useQuery({ queryKey: [...QK.subscription(), currentUserId ?? null], queryFn: billingApi.subscription, staleTime: 60_000, retry: false });
}
export function useMySubscriptionHistory() {
  const currentUserId = useSessionStore((s) => s.user?.id);
  return useQuery({ queryKey: [...QK.subHistory(), currentUserId ?? null], queryFn: billingApi.subscriptionHistory, staleTime: 60_000 });
}
export function useBillingPlans() {
  return useQuery({ queryKey: QK.billingPlans(), queryFn: billingApi.plans, staleTime: 300_000 });
}

// Every billing surface a captured payment can change. Shared by the Checkout
// verification and both recovery paths so they can never drift apart.
function invalidateBillingAfterPayment(qc) {
  qc.invalidateQueries({ queryKey: QK.subscription() });
  qc.invalidateQueries({ queryKey: QK.subHistory() });
  qc.invalidateQueries({ queryKey: QK.billingPlans() });
  qc.invalidateQueries({ queryKey: QK.myLicensePacks() });
  qc.invalidateQueries({ queryKey: ['admin', 'billing', 'wallet'] });
  qc.invalidateQueries({ queryKey: ['admin', 'billing', 'ledger'] });
  qc.invalidateQueries({ queryKey: ['billing', 'pendingPayments'] });
}

// ─── Billing — owner-scoped pending payments ───────────────────────────────────
// Paginated through the backend (`limit`/`offset`); the browser never requests
// an unbounded payment history and never sends an owner selector.
export function useMyPendingPayments({ limit = 20, offset = 0, enabled = true } = {}) {
  const currentUserId = useSessionStore((s) => s.user?.id);
  return useQuery({
    queryKey: [...QK.pendingPayments({ limit, offset }), currentUserId ?? null],
    queryFn:  () => billingApi.pendingOrders({ limit, offset }),
    enabled,
    staleTime: 15_000,
    retry: false,
  });
}

export function useVerifyPendingPayment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (orderId) => billingApi.verifyPendingOrder(orderId),
    onSuccess: (result) => {
      qc.invalidateQueries({ queryKey: ['billing', 'pendingPayments'] });
      // Only an authoritative `paid` outcome changes entitlements.
      if (result?.outcome === 'paid') invalidateBillingAfterPayment(qc);
    },
  });
}

// ─── Billing — Operator Razorpay gateway configuration ─────────────────────────
// Operator-scoped reads/writes. The response carries configuration status and a
// masked key id only; the stored key secret is never returned by the backend and
// is therefore never cached here.
export function useOperatorPaymentGateway({ enabled = true } = {}) {
  const currentUserId = useSessionStore((s) => s.user?.id);
  return useQuery({
    queryKey: [...QK.operatorGateway(), currentUserId ?? null],
    queryFn:  billingApi.operatorGateway,
    enabled,
    staleTime: 30_000,
    retry: false,
  });
}

export function useReplaceOperatorGatewayCredentials() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ key_id, key_secret, mode }) =>
      billingApi.replaceOperatorGatewayCredentials({ key_id, key_secret, mode }),
    onSuccess: () => qc.invalidateQueries({ queryKey: QK.operatorGateway() }),
  });
}

export function useSetOperatorGatewayEnabled() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (enabled) => billingApi.setOperatorGatewayEnabled(enabled),
    onSuccess: () => qc.invalidateQueries({ queryKey: QK.operatorGateway() }),
  });
}

// ─── Identity — Operator self-service branding ────────────────────────────────
// Keyed by the authenticated user id (like the gateway hooks above) so one
// Operator's branding can never be served from cache to another account.
export function useOperatorBranding({ enabled = true } = {}) {
  const currentUserId = useSessionStore((s) => s.user?.id);
  return useQuery({
    queryKey: [...QK.operatorBranding(), currentUserId ?? null],
    queryFn:  identityApi.operatorBranding,
    enabled,
    staleTime: 30_000,
    retry: false,
  });
}

export function useUpdateOperatorBranding() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (changes) => identityApi.updateOperatorBranding(changes),
    onSuccess: () => qc.invalidateQueries({ queryKey: QK.operatorBranding() }),
  });
}
// ─── Identity — Operator own logo (white-label Phase 5C) ──────────────────────
// The preview query is keyed by the authenticated user id AND the immutable
// managed logo reference, so one Operator's preview blob can never be served
// to another account and a replacement always re-fetches fresh bytes.
// `logoReference` is used only as a cache key; it is never a URL.
export function useOperatorLogoPreview({ enabled = true, logoReference = null } = {}) {
  const currentUserId = useSessionStore((s) => s.user?.id);
  return useQuery({
    queryKey: [...QK.operatorLogoPreview(), currentUserId ?? null, logoReference ?? null],
    queryFn: ({ signal }) => identityApi.operatorLogo({ signal }),
    enabled: enabled && Boolean(logoReference),
    // References are immutable: once fetched, the bytes cannot change.
    staleTime: Infinity,
    gcTime: 60_000,
    retry: false,
  });
}

export function useUploadOperatorLogo() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (file) => identityApi.uploadOperatorLogo(file),
    onSuccess: () => qc.invalidateQueries({ queryKey: QK.operatorBranding() }),
  });
}

export function useRemoveOperatorLogo() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => identityApi.removeOperatorLogo(),
    onSuccess: () => qc.invalidateQueries({ queryKey: QK.operatorBranding() }),
  });
}

// ─── Identity — Operator own SMTP profile (white-label Phase 5D) ──────────────
// Keyed by the authenticated user id so one Operator's SMTP status can never be
// served from cache to another account. The profile holds status only.
export function useOperatorSmtpProfile({ enabled = true } = {}) {
  const currentUserId = useSessionStore((s) => s.user?.id);
  return useQuery({
    queryKey: [...QK.operatorSmtp(), currentUserId ?? null],
    queryFn:  identityApi.operatorSmtpProfile,
    enabled,
    staleTime: 30_000,
    retry: false,
  });
}

// The app password exists only in this mutation's variables while the request
// is in flight. gcTime 0 plus the caller's reset() drop it from the mutation
// cache as soon as the request settles.
export function useSaveOperatorSmtpCredentials() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ provider, app_password }) =>
      identityApi.saveOperatorSmtpCredentials({ provider, app_password }),
    gcTime: 0,
    onSuccess: () => qc.invalidateQueries({ queryKey: QK.operatorSmtp() }),
  });
}

export function useDisableOperatorSmtp() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => identityApi.disableOperatorSmtp(),
    onSuccess: () => qc.invalidateQueries({ queryKey: QK.operatorSmtp() }),
  });
}

// ─── Identity — Broker support contacts (white-label Phase 5D-D) ──────────────
// Keyed by the authenticated user AND the self/target scope so one account's or
// one customer's contacts can never be served from cache to another. No
// placeholder/previous data: a new key starts empty, so a different customer's
// contacts are never shown while this one loads or after it fails.
function isValidSupportTargetId(id) {
  return Number.isInteger(id) && id > 0;
}

export function useSupportContact(targetUserId, { enabled = true } = {}) {
  const currentUserId = useSessionStore((s) => s.user?.id);
  const isTarget = targetUserId !== undefined && targetUserId !== null;
  const scopeReady = isTarget ? isValidSupportTargetId(targetUserId) : true;
  return useQuery({
    queryKey: [
      ...QK.supportContact(),
      currentUserId ?? null,
      isTarget ? 'target' : 'self',
      isTarget ? targetUserId : null,
    ],
    queryFn: ({ signal }) =>
      identityApi.supportContact(isTarget ? targetUserId : undefined, { signal }),
    enabled: Boolean(enabled) && currentUserId != null && scopeReady,
    staleTime: 30_000,
    retry: false,
  });
}

// ─── Billing — Admin read ──────────────────────────────────────────────────────
export function useOperatorWallet(opId) {
  return useQuery({ queryKey: QK.operatorWallet(opId), queryFn: () => adminBillingApi.getOperatorWallet(opId), enabled: !!opId, staleTime: 30_000 });
}
export function useLicenseLedger(opId, params) {
  return useQuery({ queryKey: QK.licenseLedger(opId), queryFn: () => adminBillingApi.getLicenseLedger(opId, params), enabled: !!opId });
}
export function useAdminBillingPlans(params) {
  return useQuery({ queryKey: QK.adminPlans(params), queryFn: () => adminBillingApi.listPlans(params), staleTime: 30_000 });
}
export function useAdminUserSubscription(uid) {
  return useQuery({ queryKey: QK.adminUserSub(uid), queryFn: () => adminBillingApi.getUserSubscription(uid), enabled: !!uid, staleTime: 30_000 });
}
export function useAdminUserSubscriptionHistory(uid) {
  return useQuery({ queryKey: QK.adminUserSubHistory(uid), queryFn: () => adminBillingApi.getUserSubHistory(uid), enabled: !!uid });
}
export function useAdminSubscriptions(params) {
  return useQuery({ queryKey: QK.adminSubscriptions(params), queryFn: () => adminBillingApi.listSubscriptions(params), staleTime: 30_000 });
}

// ─── Billing — Admin mutations ─────────────────────────────────────────────────
export function useAllocateLicenses() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ opId, body }) => adminBillingApi.allocateLicenses(opId, body),
    onSuccess: (_, { opId }) => {
      qc.invalidateQueries({ queryKey: QK.operatorWallet(opId) });
      qc.invalidateQueries({ queryKey: QK.licenseLedger(opId) });
    },
  });
}
export function useCreatePlan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: adminBillingApi.createPlan,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'billing', 'plans'] }),
  });
}
export function useUpdatePlan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }) => adminBillingApi.updatePlan(id, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'billing', 'plans'] }),
  });
}
export function useDeletePlan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => adminBillingApi.deletePlan(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'billing', 'plans'] }),
  });
}
export function useAssignPlan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ uid, body }) => adminBillingApi.assignPlan(uid, body),
    onSuccess: (_, { uid }) => {
      qc.invalidateQueries({ queryKey: QK.adminUserSub(uid) });
      qc.invalidateQueries({ queryKey: QK.adminUserSubHistory(uid) });
      // Invalidate all operator wallets since we don't know which one was used
      qc.invalidateQueries({ queryKey: ['admin', 'billing', 'wallet'] });
    },
  });
}
export function useUpdateUserValidity() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ uid, body }) => adminBillingApi.updateUserValidity(uid, body),
    onSuccess: (_, { uid }) => {
      qc.invalidateQueries({ queryKey: ['adminIdentityUser', uid] });
      qc.invalidateQueries({ queryKey: ['adminIdentityUsers'] });
      qc.invalidateQueries({ queryKey: ['adminUsers'] });
      qc.invalidateQueries({ queryKey: QK.adminUserSub(uid) });
      qc.invalidateQueries({ queryKey: QK.adminUserSubHistory(uid) });
    },
  });
}

// ─── License Pack hooks ────────────────────────────────────────────────────────
export function useMyLicensePacks() {
  return useQuery({ queryKey: QK.myLicensePacks(), queryFn: billingApi.licensePacks, staleTime: 300_000 });
}
export function useLicensePacks(params) {
  return useQuery({ queryKey: QK.licensePacks(params), queryFn: () => adminBillingApi.listLicensePacks(params), staleTime: 60_000 });
}
export function useCreateLicensePack() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => adminBillingApi.createLicensePack(body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'billing', 'license-packs'] }),
  });
}
export function useUpdateLicensePack() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }) => adminBillingApi.updateLicensePack(id, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'billing', 'license-packs'] }),
  });
}
export function useDeactivateLicensePack() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => adminBillingApi.deleteLicensePack(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'billing', 'license-packs'] }),
  });
}
export function useAssignLicensePack() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ packId, body }) => adminBillingApi.assignLicensePack(packId, body),
    onSuccess: (_, { body }) => {
      qc.invalidateQueries({ queryKey: QK.operatorWallet(body.operator_id) });
      qc.invalidateQueries({ queryKey: QK.licenseLedger(body.operator_id) });
    },
  });
}

export function useAssignPlanToOperator() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ planId, body }) => adminBillingApi.assignPlanToOperator(planId, body),
    onSuccess: (_, { body }) => {
      qc.invalidateQueries({ queryKey: QK.operatorWallet(body.operator_id) });
      qc.invalidateQueries({ queryKey: QK.licenseLedger(body.operator_id) });
    },
  });
}

// ─── Proxy IP Binding ─────────────────────────────────────────────────────────

export function useBrokerProxyBinding(params, options = {}) {
  return useQuery({
    queryKey: QK.proxyBinding(params || null),
    queryFn:  () => brokerApi.getProxyBinding(params),
    staleTime: 60_000,
    retry: 1,
    ...options,
  });
}

export function useAdminProxyInventory() {
  return useQuery({
    queryKey: QK.adminProxyInv(),
    queryFn:  adminProxyApi.listInventory,
    staleTime: 30_000,
  });
}

export function useAdminProxyBindings(params) {
  return useQuery({
    queryKey: QK.adminProxyBind(params),
    queryFn:  () => adminProxyApi.listBindings(params),
    staleTime: 30_000,
  });
}

export function useAdminProxyCapacity(params, options = {}) {
  return useQuery({
    queryKey: QK.adminProxyCapacity(params),
    queryFn:  () => adminProxyApi.getCapacity(params),
    staleTime: 30_000,
    ...options,
  });
}

export function useAdminProxyPending(params) {
  return useQuery({
    queryKey: QK.adminProxyPend(params),
    queryFn:  () => adminProxyApi.listPending(params),
    staleTime: 30_000,
  });
}

export function useAdminProxyCooling(params = {}, options = {}) {
  return useQuery({
    queryKey: QK.adminProxyCooling(params),
    queryFn:  () => adminProxyApi.listCooling(params),
    staleTime: 30_000,
    ...options,
  });
}

export function useAdminProxyReassignmentCandidates(bindingId, options = {}) {
  return useQuery({
    queryKey: QK.adminProxyReassignmentCandidates(bindingId),
    queryFn:  () => adminProxyApi.listReplacementCandidates(bindingId),
    enabled:  !!bindingId,
    staleTime: 0,
    retry: 1,
    ...options,
  });
}

export function useCreateProxyInventory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload) => adminProxyApi.createInventory(payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: QK.adminProxyInv() }),
  });
}

// ─── Dedicated IPv6 provisioning (Phase B/C1 reservation) ──────────────────────
// `IPV6_PROVISIONING_RUN_TERMINAL_STATUSES` describe only the underlying C1
// provisioning run (host-level proxy creation); SUCCEEDED here means every
// reserved IPv6/port was provisioned by C1, not that verification/
// registration/inventory has completed. Phase E's `run.workflow.is_terminal`
// (see `useIpv6ProvisioningRun` below) is the authoritative end-to-end signal
// and is what polling/UI terminal decisions must use — this status-based
// helper is kept only for optional non-blocking UX hints (e.g. "provisioning
// is currently running for this server").
export const IPV6_PROVISIONING_RUN_TERMINAL_STATUSES = ['SUCCEEDED', 'FAILED', 'PARTIAL'];

export function isTerminalIpv6ProvisioningRunStatus(status) {
  return IPV6_PROVISIONING_RUN_TERMINAL_STATUSES.includes(status);
}

export function useAdminProxyIpv6Servers(options = {}) {
  return useQuery({
    queryKey: QK.adminProxyIpv6Servers(),
    queryFn:  adminProxyApi.listIpv6ProvisioningServers,
    staleTime: 60_000,
    ...options,
  });
}

// Phase E — discover a Proxy Server's existing nonterminal end-to-end IPv6
// workflows (backend is the source of truth; no localStorage/session map).
// Deliberately on-demand (enabled only once a server is selected) rather than
// polled continuously, to avoid querying every possible Proxy Server.
export function useAdminProxyIpv6ActiveRuns(serverId, options = {}) {
  const { enabled = true, ...rest } = options;
  return useQuery({
    queryKey: QK.adminProxyIpv6ActiveRuns(serverId),
    queryFn:  () => adminProxyApi.listIpv6ProvisioningRuns({
      proxy_server_id: Number(serverId),
      active_only: true,
    }),
    enabled:  !!serverId && enabled,
    staleTime: 10_000,
    retry: 1,
    ...rest,
  });
}

export function useCreateIpv6ProvisioningRun() {
  return useMutation({
    mutationFn: ({ payload, idempotencyKey }) => adminProxyApi.createIpv6ProvisioningRun(payload, idempotencyKey),
  });
}

export function useIpv6ProvisioningRun(runId, options = {}) {
  return useQuery({
    queryKey: QK.adminProxyIpv6Run(runId),
    queryFn:  () => adminProxyApi.getIpv6ProvisioningRun(runId),
    enabled:  !!runId,
    // Poll until the Phase E end-to-end workflow is genuinely terminal, not
    // merely when the underlying C1 provisioning run reaches SUCCEEDED/
    // PARTIAL/FAILED — verification/registration continue after that point.
    refetchInterval: (query) => (
      query.state.data?.workflow?.is_terminal ? false : P.deployment
    ),
    ...options,
  });
}

export function useUpdateProxyInventory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }) => adminProxyApi.updateInventory(id, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QK.adminProxyInv() });
      qc.invalidateQueries({ queryKey: ['admin', 'proxy', 'capacity'] });
    },
  });
}

export function useReleaseProxyBinding() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }) => adminProxyApi.releaseBinding(id, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'proxy', 'bindings'] });
      qc.invalidateQueries({ queryKey: ['admin', 'proxy', 'pending'] });
      qc.invalidateQueries({ queryKey: ['admin', 'proxy', 'cooling'] });
      qc.invalidateQueries({ queryKey: ['admin', 'proxy', 'capacity'] });
    },
  });
}

export function useReassignProxyBinding() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }) => adminProxyApi.reassignBinding(id, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'proxy', 'bindings'] });
      qc.invalidateQueries({ queryKey: ['admin', 'proxy', 'cooling'] });
      qc.invalidateQueries({ queryKey: QK.adminProxyInv() });
      qc.invalidateQueries({ queryKey: ['admin', 'proxy', 'pending'] });
      qc.invalidateQueries({ queryKey: ['admin', 'proxy', 'capacity'] });
    },
  });
}

export function useExtendProxyCooling() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }) => adminProxyApi.extendCooling(id, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'proxy', 'cooling'] });
      qc.invalidateQueries({ queryKey: ['admin', 'proxy', 'bindings'] });
      qc.invalidateQueries({ queryKey: QK.adminProxyInv() });
      qc.invalidateQueries({ queryKey: ['admin', 'proxy', 'pending'] });
      qc.invalidateQueries({ queryKey: ['admin', 'proxy', 'capacity'] });
    },
  });
}

export function useClearProxyCooling() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }) => adminProxyApi.clearCooling(id, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'proxy', 'cooling'] });
      qc.invalidateQueries({ queryKey: ['admin', 'proxy', 'bindings'] });
      qc.invalidateQueries({ queryKey: QK.adminProxyInv() });
      qc.invalidateQueries({ queryKey: ['admin', 'proxy', 'pending'] });
      qc.invalidateQueries({ queryKey: ['admin', 'proxy', 'capacity'] });
    },
  });
}

export function useRetryProxyPending() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => adminProxyApi.retryPending(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'proxy', 'pending'] });
      qc.invalidateQueries({ queryKey: ['admin', 'proxy', 'bindings'] });
    },
  });
}

export function useRetryAllProxyPending() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: adminProxyApi.retryAllPending,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'proxy', 'pending'] });
      qc.invalidateQueries({ queryKey: ['admin', 'proxy', 'bindings'] });
    },
  });
}

// ─── Payment / Razorpay hooks ──────────────────────────────────────────────────
export function useCreateOrder() {
  return useMutation({
    mutationFn: ({ item_type, item_id }) => billingApi.createOrder(item_type, item_id),
  });
}

export function useVerifyPayment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ razorpay_payment_id, razorpay_order_id, razorpay_signature }) =>
      billingApi.verifyPayment(razorpay_payment_id, razorpay_order_id, razorpay_signature),
    onSuccess: () => {
      // Refresh all billing state after a successful payment
      invalidateBillingAfterPayment(qc);
    },
  });
}

// ─── Payments ──────────────────────────────────────────────────────────────────
export function useAdminPayments(params) {
  return useQuery({
    queryKey: QK.adminPayments(params),
    queryFn:  () => adminPaymentsApi.listPayments(params),
    staleTime: 30_000,
  });
}

export function useAdminPayment(source, sourceId) {
  return useQuery({
    queryKey: QK.adminPayment(source, sourceId),
    queryFn:  () => adminPaymentsApi.getPayment(source, sourceId),
    enabled:  !!source && !!sourceId,
    staleTime: 30_000,
  });
}

// Super Admin evidence-based recovery of one billing payment order. The backend
// is the only authority: it re-reads Razorpay, never creates or charges a
// payment, and returns `paid`, `pending` or `support_required`.
export function useRecoverAdminBillingPayment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (orderId) => adminPaymentsApi.recoverBillingPayment(orderId),
    onSuccess: (result) => {
      qc.invalidateQueries({ queryKey: ['adminPayments'] });
      qc.invalidateQueries({ queryKey: ['adminPayment'] });
      qc.invalidateQueries({ queryKey: ['billing', 'pendingPayments'] });
      if (result?.outcome === 'paid') invalidateBillingAfterPayment(qc);
    },
  });
}

// ─── Website Leads ─────────────────────────────────────────────────────────────
export function useWebsiteLeads(params) {
  return useQuery({
    queryKey: QK.websiteLeads(params),
    queryFn:  () => adminWebsiteApi.listLeads(params),
    staleTime: 30_000,
  });
}

export function useWebsiteLead(id) {
  return useQuery({
    queryKey: QK.websiteLead(id),
    queryFn:  () => adminWebsiteApi.getLead(id),
    enabled:  !!id,
    staleTime: 30_000,
  });
}

export function useUpdateWebsiteLead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }) => adminWebsiteApi.updateLead(id, payload),
    onSuccess: (_, { id }) => {
      qc.invalidateQueries({ queryKey: QK.websiteLead(id) });
      qc.invalidateQueries({ queryKey: ['websiteLeads'] });
    },
  });
}

export function useMarkWebsiteLeadSpam() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => adminWebsiteApi.markSpam(id),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: QK.websiteLead(data.id) });
      qc.invalidateQueries({ queryKey: ['websiteLeads'] });
    },
  });
}


export function useSendWebsiteLeadEmail() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }) => adminWebsiteApi.sendEmail(id, payload),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: QK.websiteLead(data.id) });
      qc.invalidateQueries({ queryKey: ['websiteLeads'] });
    },
  });
}

export function useCloseWebsiteLead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => adminWebsiteApi.closeLead(id),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: QK.websiteLead(data.id) });
      qc.invalidateQueries({ queryKey: ['websiteLeads'] });
    },
  });
}

// ─── Custom Development Strategy Requests (Admin / super_admin only) ──────────
// Separate backend domain from Website Leads — see api/client.js adminCustomDevelopmentApi.
export function useCustomDevRequests(params) {
  return useQuery({
    queryKey: QK.customDevRequests(params),
    queryFn:  () => adminCustomDevelopmentApi.listRequests(params),
    staleTime: 30_000,
  });
}

export function useCustomDevRequest(requestReference) {
  return useQuery({
    queryKey: QK.customDevRequest(requestReference),
    queryFn:  () => adminCustomDevelopmentApi.getRequest(requestReference),
    enabled:  !!requestReference,
    staleTime: 30_000,
  });
}

export function useCustomDevMessages(requestReference, params) {
  return useQuery({
    queryKey: QK.customDevMessages(requestReference, params),
    queryFn:  () => adminCustomDevelopmentApi.listMessages(requestReference, params),
    enabled:  !!requestReference,
    staleTime: 15_000,
  });
}

export function useSendCustomDevMessage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ requestReference, formData, idempotencyKey }) =>
      adminCustomDevelopmentApi.sendMessage(requestReference, formData, idempotencyKey),
    onSuccess: (_, { requestReference }) => {
      qc.invalidateQueries({ queryKey: ['customDevMessages', requestReference] });
    },
  });
}

// Blob-download mutations. Modeled as mutations (not queries) since each click
// is a one-off, non-cached fetch of protected file bytes.
export function useDownloadCustomDevStrategyFile() {
  return useMutation({
    mutationFn: ({ requestReference, fileReference }) =>
      adminCustomDevelopmentApi.downloadStrategyFile(requestReference, fileReference),
  });
}

export function useDownloadCustomDevAgreementPdf() {
  return useMutation({
    mutationFn: ({ agreementReference }) => adminCustomDevelopmentApi.downloadAgreementPdf(agreementReference),
  });
}

export function useDownloadCustomDevMessageFile() {
  return useMutation({
    mutationFn: ({ requestReference, messageReference, fileReference }) =>
      adminCustomDevelopmentApi.downloadMessageFile(requestReference, messageReference, fileReference),
  });
}

// ─── Website Articles ───────────────────────────────────────────────────────────
export function useWebsiteArticles(params) {
  return useQuery({
    queryKey: QK.websiteArticles(params),
    queryFn:  () => adminWebsiteArticlesApi.listArticles(params),
    staleTime: 30_000,
  });
}

export function useWebsiteArticle(id) {
  return useQuery({
    queryKey: QK.websiteArticle(id),
    queryFn:  () => adminWebsiteArticlesApi.getArticle(id),
    enabled:  !!id,
    staleTime: 30_000,
  });
}

export function useCreateWebsiteArticle() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload) => adminWebsiteArticlesApi.createArticle(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['websiteArticles'] });
    },
  });
}

export function useUpdateWebsiteArticle() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }) => adminWebsiteArticlesApi.updateArticle(id, payload),
    onSuccess: (_, { id }) => {
      qc.invalidateQueries({ queryKey: QK.websiteArticle(id) });
      qc.invalidateQueries({ queryKey: ['websiteArticles'] });
    },
  });
}

export function usePublishWebsiteArticle() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => adminWebsiteArticlesApi.publishArticle(id),
    onSuccess: (_, id) => {
      qc.invalidateQueries({ queryKey: QK.websiteArticle(id) });
      qc.invalidateQueries({ queryKey: ['websiteArticles'] });
    },
  });
}

export function useUnpublishWebsiteArticle() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => adminWebsiteArticlesApi.unpublishArticle(id),
    onSuccess: (_, id) => {
      qc.invalidateQueries({ queryKey: QK.websiteArticle(id) });
      qc.invalidateQueries({ queryKey: ['websiteArticles'] });
    },
  });
}

export function useDeleteWebsiteArticle() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => adminWebsiteArticlesApi.deleteArticle(id),
    onSuccess: (_, id) => {
      qc.removeQueries({ queryKey: QK.websiteArticle(id) });
      qc.invalidateQueries({ queryKey: ['websiteArticles'] });
    },
  });
}

export function useUploadWebsiteArticleImage() {
  return useMutation({
    mutationFn: ({ file, imageType }) => adminWebsiteArticlesApi.uploadArticleImage(file, imageType),
  });
}

export function useImportWebsiteArticleMarkdown() {
  return useMutation({
    mutationFn: (file) => adminWebsiteArticlesApi.importMarkdownArticle(file),
  });
}

// ─── Google Reviews (super-admin, Stage 1B) ─────────────────────────────────────
export function useGoogleReviewsStatus() {
  return useQuery({
    queryKey: QK.googleReviewsStatus(),
    queryFn:  () => adminGoogleReviewsApi.getStatus(),
    staleTime: 30_000,
  });
}

export function useGoogleReviewSettings() {
  return useQuery({
    queryKey: QK.googleReviewSettings(),
    queryFn:  () => adminGoogleReviewsApi.getSettings(),
    staleTime: 30_000,
  });
}

export function useUpdateGoogleReviewSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload) => adminGoogleReviewsApi.updateSettings(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QK.googleReviewSettings() });
      qc.invalidateQueries({ queryKey: QK.googleReviewsStatus() });
    },
  });
}

export function useGoogleReviews(params) {
  return useQuery({
    queryKey: QK.googleReviews(params),
    queryFn:  () => adminGoogleReviewsApi.listReviews(params),
    staleTime: 30_000,
  });
}

export function useGoogleReview(id) {
  return useQuery({
    queryKey: QK.googleReview(id),
    queryFn:  () => adminGoogleReviewsApi.getReview(id),
    enabled:  !!id,
    staleTime: 30_000,
  });
}

export function useRequestGoogleReviewSync() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload) => adminGoogleReviewsApi.requestSync(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QK.googleReviewsStatus() });
      qc.invalidateQueries({ queryKey: ['admin', 'googleReviews', 'reviews'] });
      qc.invalidateQueries({ queryKey: QK.googleReviewLocations() });
    },
  });
}

export function useGoogleReviewLocations() {
  return useQuery({
    queryKey: QK.googleReviewLocations(),
    queryFn:  () => adminGoogleReviewsApi.listLocations(),
    staleTime: 30_000,
  });
}

export function useStartGoogleReviewsOAuth() {
  // No invalidation: on success the browser navigates away to Google.
  return useMutation({
    mutationFn: () => adminGoogleReviewsApi.startOAuth(),
  });
}

export function useDisconnectGoogleReviews() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => adminGoogleReviewsApi.disconnect(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QK.googleReviewsStatus() });
      qc.invalidateQueries({ queryKey: QK.googleReviewLocations() });
      qc.invalidateQueries({ queryKey: ['admin', 'googleReviews', 'reviews'] });
    },
  });
}

export function useDiscoverGoogleReviewLocations() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => adminGoogleReviewsApi.discoverLocations(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QK.googleReviewLocations() });
      qc.invalidateQueries({ queryKey: QK.googleReviewsStatus() });
    },
  });
}

export function useSelectGoogleReviewLocation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => adminGoogleReviewsApi.selectLocation(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QK.googleReviewLocations() });
      qc.invalidateQueries({ queryKey: QK.googleReviewsStatus() });
      qc.invalidateQueries({ queryKey: ['admin', 'googleReviews', 'reviews'] });
    },
  });
}
